import { useCallback, useMemo, useState } from "react";
import apiClient, {
  setAuthToken,
  AUTH_TOKEN_STORAGE_KEY,
  LOCATION_STORAGE_KEY,
  ORGANIZATION_STORAGE_KEY,
} from "@/lib/api-client";

/**
 * Impersonación de superadmin ("Acceder como").
 *
 * Claves de sessionStorage (mismo storage que el token de auth normal, ver
 * AuthContext — sobrevive a refresh, no a cierre de pestaña):
 * - `rm_impersonation_origin_token`: token JWT del superadmin, guardado mientras
 *   dura la impersonación para poder restaurarlo al salir.
 * - `rm_impersonation_target`: nombre/email del usuario impersonado, solo para
 *   mostrar en el banner (no se usa para lógica de seguridad).
 *
 * El flag de "hay impersonación activa" es la simple presencia del token de
 * origen guardado — no hace falta decodificar el JWT activo.
 *
 * Exportadas para que `AuthContext.tsx` pueda limpiarlas defensivamente en
 * login/logout sin duplicar los strings literales.
 */
export const IMPERSONATION_ORIGIN_TOKEN_KEY = "rm_impersonation_origin_token";
export const IMPERSONATION_TARGET_KEY = "rm_impersonation_target";
const ORIGIN_TOKEN_KEY = IMPERSONATION_ORIGIN_TOKEN_KEY;
const TARGET_KEY = IMPERSONATION_TARGET_KEY;

/**
 * Inicia la impersonación: guarda el token de superadmin actual, activa el
 * token recibido del endpoint `/admin/impersonate/:userId`, limpia el
 * contexto de ubicación/organización (para que se re-derive de la org del
 * usuario impersonado) y recarga la app.
 */
export function startImpersonation(token: string, targetLabel: string) {
  const currentToken = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  if (currentToken) {
    sessionStorage.setItem(ORIGIN_TOKEN_KEY, currentToken);
  }
  sessionStorage.setItem(TARGET_KEY, targetLabel);

  setAuthToken(token);
  sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);

  // El superadmin puede tener seleccionada una ubicación/organización propia:
  // hay que limpiarla para que LocationContext la re-derive para la org impersonada.
  localStorage.removeItem(LOCATION_STORAGE_KEY);
  localStorage.removeItem(ORGANIZATION_STORAGE_KEY);

  window.location.href = "/";
}

export function useImpersonation() {
  const [isStopping, setIsStopping] = useState(false);

  // sessionStorage es síncrono: basta leerlo una vez al montar. El flujo
  // completo (empezar/terminar impersonación) siempre hace un reload de la
  // app, así que no hace falta que esto sea reactivo entre renders.
  const isImpersonating = useMemo(
    () => !!sessionStorage.getItem(ORIGIN_TOKEN_KEY),
    [],
  );
  const targetLabel = useMemo(() => sessionStorage.getItem(TARGET_KEY), []);

  /**
   * Restaura la sesión de superadmin. Prioriza `freshToken` (el token rotado
   * que devuelve `POST /admin/impersonate/stop` cuando responde OK); el token
   * guardado en `rm_impersonation_origin_token` es solo el FALLBACK si la
   * llamada a stop falla o no devuelve token (p.ej. token de impersonación ya
   * caducado a las 2h) — así nunca dejamos al usuario atrapado.
   */
  const restoreOriginSession = useCallback((freshToken?: string | null) => {
    const originToken = sessionStorage.getItem(ORIGIN_TOKEN_KEY);
    sessionStorage.removeItem(ORIGIN_TOKEN_KEY);
    sessionStorage.removeItem(TARGET_KEY);
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY);

    const tokenToRestore = freshToken || originToken;

    if (tokenToRestore) {
      setAuthToken(tokenToRestore);
      sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, tokenToRestore);
    } else {
      // No debería pasar, pero si no hay token que restaurar, no dejamos
      // al usuario atrapado con un token de impersonación caducado: cerramos sesión.
      setAuthToken(null);
      sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }

    window.location.href = "/";
  }, []);

  const stop = useCallback(async () => {
    setIsStopping(true);
    let freshToken: string | null = null;
    try {
      // Cierra el log de auditoría en el backend, que además rota y devuelve un
      // token FRESCO de superadmin (y pone la cookie). Si el token de
      // impersonación ya caducó (2h) o la llamada falla, `freshToken` queda
      // `null` y restauramos con el guardado como fallback (ver más abajo).
      const response = await apiClient.post<{ token?: string }>(
        "/admin/impersonate/stop",
      );
      freshToken = response.data?.token ?? null;
    } catch {
      // Ignorado a propósito.
    } finally {
      restoreOriginSession(freshToken);
    }
  }, [restoreOriginSession]);

  return { isImpersonating, targetLabel, stop, isStopping };
}
